<template>
  <Loading v-if="isLoading" />
  <div v-else-if="!isLoading && meetingInstance && activeVenue">
    <popup-add-task category="sm" ref="popupAddTask" />

    <div v-if="showCompletedScreen">
      <vs-row vs-justify="center" class="my-4">
        <h3>{{ meetingInstance.title }}</h3>
      </vs-row>
      <vx-card>
        <div style="min-height: 400px" class="flex flex-col justify-center items-center text-center">
          <div>
            <b>Good job!</b>
          </div>
          <div class="mb-2">
            Your meeting is now finalized, and a summary has been sent to all relevant parties. <br />
            Love for People - Be Fearless - Act as Founders
          </div>
        </div>
      </vx-card>
    </div>

    <!-- Start step -->
    <div v-else-if="activeStep == -2">
      <vs-row class="my-4" vs-justify="center">
        <h3 class="pl-8">{{ meetingInstance.meeting.title }} for {{ activeVenue.name }}</h3>
        <vs-chip class="ml-2">
          {{ whenString }}
        </vs-chip>
      </vs-row>
      <vs-row>
        <vs-col vs-w="6" vs-sm="12" class="p-4">
          <vx-card>
            <vs-row vs-justify="center" class="mb-4">
              <h3>Purpose</h3>
            </vs-row>
            <div class="custom-html-wrapper" v-html="meetingInstance.meeting.description.purpose"></div>
          </vx-card>
        </vs-col>

        <vs-col vs-w="6" vs-sm="12" class="p-4">
          <vx-card>
            <vs-row vs-justify="center" class="mb-4">
              <h3>Participants</h3>
            </vs-row>
            <div class="custom-html-wrapper" v-html="meetingInstance.meeting.description.participants"></div>
          </vx-card>
        </vs-col>

        <vs-col vs-w="6" vs-sm="12" class="p-4">
          <vx-card>
            <vs-row vs-justify="center" class="mb-4">
              <h3>Other information</h3>
            </vs-row>
            <div class="custom-html-wrapper" v-html="meetingInstance.meeting.description.otherInformation"></div>
          </vx-card>
        </vs-col>
        <vs-col vs-w="6" vs-sm="12" class="p-8">
          <div class="vx-row">
            <div class="vx-col w-1/5">Venue</div>
            <div class="vx-col w-4/5">
              <venue-selector v-model="meetingInstance.venueId" :multiple="false" :deactivated="true" />
            </div>
          </div>
          <div class="vx-row mt-2" v-if="meetingInstance.meeting.type == 'Weekly' || meetingInstance.meeting.type == 'BiWeekly'">
            <div class="vx-col w-1/5">Week number</div>
            <div class="vx-col w-4/5 flex flex-start">
              <vs-input-number :min="1" :max="52" v-model="meetingInstance.when" />
            </div>
          </div>
          <div class="vx-row mt-2" v-else-if="meetingInstance.meeting.type == 'Monthly'">
            <div class="vx-col w-1/5">Month</div>
            <div class="vx-col w-4/5 flex flex-start">
              <vs-select v-model="meetingInstance.when">
                <vs-select-item
                  v-for="(month, index) in [
                    'January',
                    'February',
                    'March',
                    'April',
                    'May',
                    'June',
                    'July',
                    'August',
                    'September',
                    'October',
                    'November',
                    'December'
                  ]"
                  :key="month"
                  :value="index + 1"
                  :text="month"
                />
              </vs-select>
            </div>
          </div>
          <div class="vx-row mt-2" v-else-if="meetingInstance.meeting.type == 'Quarterly'">
            <div class="vx-col w-1/5">Quarter</div>
            <div class="vx-col w-4/5 flex flex-start">
              <vs-select v-model="meetingInstance.when">
                <vs-select-item v-for="(month, index) in ['Q1', 'Q2', 'Q3', 'Q4']" :key="month" :value="index + 1" :text="month" />
              </vs-select>
            </div>
          </div>
          <div class="vx-row mt-2" v-else-if="meetingInstance.meeting.type == 'BiAnnually'">
            <div class="vx-col w-1/5">Biannual period</div>
            <div class="vx-col w-4/5 flex flex-start">
              <vs-select v-model="meetingInstance.when">
                <vs-select-item v-for="(month, index) in ['H1', 'H2']" :key="month" :value="index" :text="month" />
              </vs-select>
            </div>
          </div>
          <div class="vx-row mt-2">
            <div class="vx-col w-1/5">Year</div>
            <div class="vx-col w-4/5 flex flex-start">
              <vs-input-number :min="2020" :max="2100" v-model="meetingInstance.whenYear" />
            </div>
          </div>
          <div class="vx-row mt-2">
            <div class="vx-col w-1/5">Facilitator</div>
            <div class="vx-col w-4/5 flex flex-start items-start">
              <div v-if="editingFacilitator || meetingInstance.facilitator == null" class="flex items-center">
                <IdmUserSearcher @onHit="onFacilitatorSelected" placeholder="Search for a facilitator..." class="min-w-sm" />
                <vs-button @click="editingFacilitator = false" type="flat" class="px-3 py-2">
                  <feather-icon icon="XIcon" svgClasses="w-5 h-5" />
                </vs-button>
              </div>

              <div v-else class="flex items-center">
                <vs-chip color="green" class="ml-2 mt-2 instructor-chip">{{ meetingInstance.facilitator.displayName }}</vs-chip>
                <vs-button v-if="isEditable" @click="editingFacilitator = true" type="flat" class="px-3 py-2">
                  <feather-icon icon="EditIcon" svgClasses="w-5 h-5" />
                </vs-button>
              </div>
            </div>
          </div>
          <div class="vx-row mt-2">
            <div class="vx-col w-1/5">Attendees</div>
            <div class="vx-col w-4/5 flex flex-start items-start">
              <div class="flex flex-col flex-1">
                <vs-row v-if="isEditable">
                  <div class="flex items-center w-full">
                    <IdmUserSearcher @onHit="onAttendeeSelected" placeholder="Search for an attendee..." class="min-w-sm w-full" />
                  </div>
                </vs-row>
                <vs-row>
                  <vs-list>
                    <vs-list-item :title="user.displayName" :subtitle="user.email" v-for="user in meetingInstance.attendees" :key="user.id">
                      <template slot="avatar">
                        <vs-avatar />
                      </template>
                      <vs-button v-if="isEditable" @click="removeAttendee(user)" type="flat" class="px-3 py-2">
                        <feather-icon icon="XIcon" svgClasses="w-5 h-5" />
                      </vs-button>
                    </vs-list-item>
                  </vs-list>
                </vs-row>
              </div>
            </div>
          </div>
        </vs-col>
      </vs-row>
      <!-- <vs-row vs-justify="space-between" class="mt-8 px-4">
      <vs-button type="flat"> Back </vs-button>
      <vs-button size="large" class="w-full max-w-sm">Start meeting</vs-button>
      <vs-button class="opacity-0 pointer-events-none"> Back </vs-button>
    </vs-row> -->
    </div>

    <!-- Agenda -->
    <div v-else-if="activeStep == -1" class="flex justify-center">
      <vs-card class="shadow-lg font-lg text-white" style="width: 36rem; background-color: rgb(23, 49, 45)">
        <vs-row vs-justify="center">
          <div class="flex flex-col items-center mt-8">
            <div>
              <img width="80" :src="LogoTagline" />
            </div>
            <div>
              <h1 class="text-white">Agenda</h1>
            </div>
            <div class="font-bold text-grey mt-2">
              {{ whenString }} · {{ meetingInstance.facilitator.displayName }} ·
              {{ meetingInstance.venueName }}
            </div>
          </div>
        </vs-row>
        <div class="flex flex-col items-start pt-6 pl-6">
          <div v-for="(step, idx) in meetingInstance.meeting.meetingSteps" :key="step.id" class="flex flex-col items-start">
            <div v-if="idx == 0 || step.category != meetingInstance.meeting.meetingSteps[idx - 1].category" class="mt-5">
              <b class="text-lg">
                {{ step.category }}
              </b>
            </div>
            <li class="text-base">
              {{ step.title }}
            </li>
          </div>
        </div>
      </vs-card>
    </div>

    <!-- Summary -->
    <div v-else-if="activeStep == meetingInstance.meeting.meetingSteps.length">
      <!-- <div v-if="true"> -->
      <vs-row vs-justify="center" class="my-4">
        <h3>Meeting summary</h3>
      </vs-row>
      <vs-row>
        <vs-col vs-w="8" vs-sm="12" class="pr-2 pb-4">
          <vx-card>
            <vs-row vs-justify="center" class="mb-4">
              <h3>Info</h3>
            </vs-row>
            <div>
              Below is a summary of the answered questions for this meeting. Select recipients to the right, which will receive the summary
              below on their specified mail.
            </div>
          </vx-card>
        </vs-col>

        <vs-col vs-w="4" vs-sm="12" class="pl-2 pb-4">
          <vx-card>
            <vs-row vs-justify="center" class="mb-4">
              <h3>Recipients</h3>
            </vs-row>

            <vs-row vs-justify="center" class="mb-4" v-if="isEditable">
              <vs-button @click="copyAttendeesToRecipientList"> Copy all attendees to recipient list </vs-button>
            </vs-row>

            <div class="flex flex-col flex-1">
              <vs-row v-if="isEditable">
                <div class="flex items-center w-full">
                  <IdmUserSearcher @onHit="onRecepientSelected" placeholder="Search for a recipient..." class="min-w-sm w-full" />
                </div>
              </vs-row>
              <vs-row>
                <vs-list>
                  <vs-list-item :title="user.displayName" :subtitle="user.email" v-for="user in meetingInstance.recipients" :key="user.id">
                    <template slot="avatar">
                      <vs-avatar />
                    </template>
                    <vs-button v-if="isEditable" @click="removeRecepient(user)" type="flat" class="px-3 py-2">
                      <feather-icon icon="XIcon" svgClasses="w-5 h-5" />
                    </vs-button>
                  </vs-list-item>
                </vs-list>
              </vs-row>
            </div>
          </vx-card>
        </vs-col>
      </vs-row>

      <vs-row
        ><vx-card>
          <div class="flex flex-col pt-2">
            <div class="mb-4">
              <h3>Facilitator</h3>
              <ul class="ml-8">
                <li v-if="meetingInstance.facilitator">
                  <b>{{ meetingInstance.facilitator.displayName }}</b> ({{ meetingInstance.facilitator.email }})
                </li>
              </ul>
            </div>
            <div class="mb-4">
              <h3>Attendees</h3>
              <ul class="ml-8">
                <li v-for="user in meetingInstance.attendees" :key="user.id">
                  <b>{{ user.displayName }}</b> ({{ user.email }})
                </li>
              </ul>
            </div>

            <div v-for="step in meetingInstance.meeting.meetingSteps" :key="step.id" class="py-2 mb-4">
              <h3 class="mb-1">{{ step.category }}: {{ step.title }}</h3>
              <div class="ml-5">
                <component :is="stepTypeToComponentSummary(step.type)" :step="step" :meetingInstance="meetingInstance"> </component>
              </div>
            </div>
          </div> </vx-card
      ></vs-row>
    </div>

    <div v-else v-for="(step, idx) in meetingInstance.meeting.meetingSteps" :key="step.id" :title="step.title">
      <div v-if="idx == activeStep">
        <vs-row vs-justify="space-between" class="mb-4">
          <vs-button class="opacity-0 pointer-events-none" icon="add"> Venue Task</vs-button>
          <h3>{{ step.category }}: {{ step.title }}</h3>
          <vs-button @click="$refs.popupAddTask.show(moment())" icon="add"> Venue Task</vs-button>
        </vs-row>
        <component :is="stepTypeToComponent(step.type)" :step="step" :meetingInstance="meetingInstance"> </component>
      </div>
    </div>

    <!-- Button row -->
    <vs-row v-if="!meetingInstance.isStarted" vs-justify="center" class="mt-8 px-4">
      <vs-button
        v-if="hasPermissionForEntity('vp.vp.meetings.edit', meetingInstance.venueId)"
        size="large"
        class="w-full max-w-sm"
        @click="startMeeting"
        :disabled="
          meetingInstance.when < 1 || !meetingInstance.facilitator || !meetingInstance.attendees || meetingInstance.attendees.length == 0
        "
        >Start meeting</vs-button
      >
    </vs-row>
    <vs-row v-else-if="!showCompletedScreen" vs-justify="space-between" class="mt-8 pb-8">
      <vs-button :class="{ 'opacity-0 pointer-events-none': activeStep < -1 }" type="border" @click="onClickBack"> Back </vs-button>
      <vs-button v-if="activeStep < meetingInstance.meeting.meetingSteps.length" @click="onClickNext"> Next </vs-button>
      <vs-button
        v-if="
          isEditable &&
          activeStep == meetingInstance.meeting.meetingSteps.length &&
          hasPermissionForEntity('vp.vp.meetings.edit', meetingInstance.venueId)
        "
        @click="onClickEndMeeting"
      >
        End meeting and send emails
      </vs-button>
    </vs-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import VenueSelector from "@/components/VenueSelector.vue";
import IdmUserSearcher from "@/components/IdmUserSearcher.vue";
import PopupAddTask from "@/views/modules/vp/venueTasks/components/PopupAddTask.vue";

import PowerBiStep from "./Components/MeetingSteps/PowerBiStep.vue";
import EmployeeReportStep from "./Components/MeetingSteps/EmployeeReportStep.vue";
import SummaryStep from "./Components/MeetingSteps/SummaryStep.vue";
import FinalReviewStep from "./Components/MeetingSteps/FinalReviewStep.vue";
import VenueTaskReviewStep from "./Components/MeetingSteps/VenueTaskReviewStep.vue";

import PowerBiStepSummary from "./Components/MeetingStepsSummary/PowerBiStepSummary.vue";
import SummaryStepSummary from "./Components/MeetingStepsSummary/SummaryStepSummary.vue";
import FinalReviewStepSummary from "./Components/MeetingStepsSummary/FinalReviewStepSummary.vue";
import VenueTaskReviewStepSummary from "./Components/MeetingStepsSummary/VenueTaskReviewStepSummary.vue";

import LogoTagline from "@/assets/images/rekom_logo_clean.svg";

export default {
  components: {
    VenueSelector,
    IdmUserSearcher,

    PowerBiStep,
    EmployeeReportStep,
    SummaryStep,
    FinalReviewStep,
    VenueTaskReviewStep,

    PowerBiStepSummary,
    SummaryStepSummary,
    FinalReviewStepSummary,
    VenueTaskReviewStepSummary,

    PopupAddTask
  },
  data() {
    return {
      editingFacilitator: false,
      showCompletedScreen: false,

      activeStep: -2,

      moment,
      LogoTagline
    };
  },
  async mounted() {},
  methods: {
    getWeekNumber(d) {
      // Copy date so don't modify original
      d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      // Set to nearest Thursday: current date + 4 - current day number
      // Make Sunday's day number 7
      d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
      // Get first day of year
      var yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      // Calculate full weeks to nearest Thursday
      var weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
      // Return array of year and week number
      return [d.getUTCFullYear(), weekNo];
    },

    stepTypeToComponent(stepType) {
      switch (stepType) {
        case "powerBi":
          return "PowerBiStep";
        case "employeesReport":
          return "EmployeeReportStep";
        case "summary":
          return "SummaryStep";
        case "finalReview":
          return "FinalReviewStep";
        case "venueTaskReview":
          return "VenueTaskReviewStep";
        default:
          return null;
      }
    },
    stepTypeToComponentSummary(stepType) {
      return this.stepTypeToComponent(stepType) + "Summary";
    },

    onFacilitatorSelected(selectedItem) {
      var userInfo = {
        id: selectedItem.userId,
        displayName: selectedItem.firstName + " " + selectedItem.lastName,
        email: selectedItem.email
      };

      this.meetingInstance.facilitator = userInfo;
      this.editingFacilitator = false;
    },

    onAttendeeSelected(selectedItem) {
      if (this.meetingInstance.attendees == null) this.meetingInstance.attendees = [];

      var userInfo = {
        id: selectedItem.userId,
        displayName: selectedItem.firstName + " " + selectedItem.lastName,
        email: selectedItem.email
      };

      this.meetingInstance.attendees.push(userInfo);
    },

    removeAttendee(user) {
      console.log("Remove attendee", user);
      this.meetingInstance.attendees = this.meetingInstance.attendees.filter(e => e.id != user.id);
    },

    async copyAttendeesToRecipientList() {
      console.log("Copying attendees to recipient list");
      this.meetingInstance.attendees.forEach(attendee => {
        // If not already in recipient list, add
        if (!this.meetingInstance.recipients.find(recipient => recipient.id == attendee.id)) {
          this.meetingInstance.recipients.push(attendee);
        }
      });
      await this.onClickSave();
    },

    async onRecepientSelected(selectedItem) {
      if (this.meetingInstance.recipients == null) this.meetingInstance.recipients = [];

      var userInfo = {
        id: selectedItem.userId,
        displayName: selectedItem.firstName + " " + selectedItem.lastName,
        email: selectedItem.email
      };

      console.log("Add recipients", userInfo, this.meetingInstance.recipients);

      this.meetingInstance.recipients.push(userInfo);
      await this.onClickSave();
    },

    async removeRecepient(user) {
      console.log("Remove recepient", user);
      this.meetingInstance.recipients = this.meetingInstance.recipients.filter(e => e.id != user.id);
      await this.onClickSave();
    },

    onClickBack() {
      if (this.meetingInstance.isEditable) this.onClickSave();
      this.activeStep--;
    },
    onClickNext() {
      if (this.meetingInstance.isEditable) this.onClickSave();
      this.activeStep++;
    },
    async onClickSave() {
      console.log("Clicked save", this.meetingInstance);
      if (!this.hasPermissionForEntity("vp.vp.meetings.edit", this.meetingInstance.venueId)) {
        console.log("No permission to save the meeting");
        return;
      }

      //Call service
      try {
        this.$vs.loading({ text: "Saving..." });
        await this.$store.dispatch("meetingInstances/update", this.meetingInstance);
        // this.$vs.notify({
        //   color: "green",
        //   title: "Meeting saved",
        //   text: "Meeting updated successfully",
        // });
      } catch (err) {
        console.error(err);

        this.$vs.notify({
          color: "danger",
          title: "Error saving meeting",
          text: err,
          time: 4000
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async startMeeting() {
      console.log("Clicked start meeting", this.meetingInstance);

      //Call service
      try {
        this.$vs.loading();
        await this.$store.dispatch("meetingInstances/startMeeting", {
          meetingInstanceId: this.meetingInstance.id,
          facilitator: this.meetingInstance.facilitator,
          attendees: this.meetingInstance.attendees,
          when: this.meetingInstance.when
        });
        this.$vs.notify({
          color: "green",
          title: "Meeting started",
          text: "Meeting started successfully"
        });
        this.meetingInstance.status = "Active";
        this.meetingInstance.isStarted = true;
      } catch (err) {
        console.error(err);

        this.$vs.notify({
          color: "danger",
          title: "Error starting meeting",
          text: err,
          time: 4000
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    async onClickEndMeeting() {
      console.log("Clicked end meeting", this.meetingInstance);

      //Call service
      try {
        this.$vs.loading();
        await this.$store.dispatch("meetingInstances/endMeeting", {
          meetingInstanceId: this.meetingInstance.id
        });
        this.$vs.notify({
          color: "green",
          title: "Meeting ended",
          text: "Meeting ended successfully"
        });

        this.showCompletedScreen = true;
      } catch (err) {
        console.error(err);

        this.$vs.notify({
          color: "danger",
          title: "Error ending meeting",
          text: err,
          time: 4000
        });
      } finally {
        this.$vs.loading.close();
      }
    }
  },
  computed: {
    ...mapGetters({
      meetingInstance: "meetingInstances/meetingInstance",
      activeVenue: "venues/activeVenue",
      isLoading: "meetingInstances/isLoading"
    }),

    isEditable() {
      return this.meetingInstance.isEditable;
    },

    whenString() {
      return this.$store.getters["meetingInstances/whenString"](this.meetingInstance);
    }
  },
  async created() {
    var meetingInstanceId = this.$route.params.id;

    await this.$store.dispatch("meetingInstances/get", meetingInstanceId);

    if (!this.meetingInstance.isEditable) {
      this.activeStep = this.meetingInstance.meeting.meetingSteps.length;
    } else if (this.meetingInstance.isStarted) {
      this.activeStep = -1;
    }
  }
};
</script>

<style lang="scss">
.custom-html-wrapper {
  ul {
    margin-top: 0.8rem;
    margin-left: 2.4rem;
    list-style-type: disc !important;
  }
}
</style>
